import { 
  SearchIcon, 
  BookCheckIcon, 
  UserIcon, 
  DatabaseIcon, 
  HomeIcon,
  SettingsIcon,
  InfoIcon,
  MessageSquareIcon,
  FileTextIcon,
  CompassIcon,
  ScaleIcon,
  ShieldIcon,
  ShieldCheckIcon,
  TrendingUpIcon,
  BarChart3Icon,
  ScrollTextIcon,
  CreditCardIcon,
  LogOutIcon,
  ClipboardListIcon,
  BookOpenIcon,
  LayoutGridIcon,
  MapPinIcon,
  MapIcon,
  Package,
  ActivityIcon,
  Megaphone,
  Gamepad2,
  Smartphone,
  Building2,
  Code2Icon,
  UsersIcon
} from 'lucide-react';
import { DashboardIcon, CalculatorIcon } from '@/components/icons';

export interface NavItem {
  path: string;
  icon: any;
  label: string;
  fullName: string;
  isCustomIcon: boolean;
  category: string;
  requiredTier: 'freemium' | 'pro' | 'investor' | 'agent_pro' | 'enterprise';
  isPro: boolean;
  isLogout?: boolean;
  ariaLabel?: string;
  keyboardShortcut?: string;
  isProFeature?: boolean;
  requiredRole?: string[];
}

// Tool subgroup definitions for collapsible organization
export const toolsSubgroups = {
  data_tools: {
    label: 'Data Tools',
    paths: ['/analytics', '/scraping-dashboard', '/system-diagnostic', '/clara-analytics']
  },
  property_tools: {
    label: 'Property Tools',
    paths: ['/floor-plans', '/lots', '/plat-explorer']
  },
  reports: {
    label: 'Reports & Forms',
    paths: ['/home-comparison-email', '/contract-ready-form']
  }
};

export const navItems: NavItem[] = [
  {
    path: '/dashboard',
    icon: DashboardIcon,
    label: 'Dashboard',
    fullName: 'Dashboard',
    isCustomIcon: true,
    category: 'freemium',
    requiredTier: 'freemium',
    isPro: false
  },
  {
    path: '/calculator',
    icon: CalculatorIcon,
    label: 'Estimator Engine',
    fullName: 'Estimator Engine',
    isCustomIcon: true,
    category: 'freemium',
    requiredTier: 'freemium',
    isPro: false
  },
  {
    path: '/reverse-piti-calculator',
    icon: TrendingUpIcon,
    label: 'Reverse PITI',
    fullName: 'Reverse PITI Calculator',
    isCustomIcon: false,
    category: 'freemium',
    requiredTier: 'freemium',
    isPro: false
  },
  {
    path: '/calculator-mobile',
    icon: Smartphone,
    label: 'Estimator (Mobile)',
    fullName: 'Mobile Estimator Engine',
    isCustomIcon: false,
    category: 'mobile_suite',
    requiredTier: 'freemium',
    isPro: false
  },
  {
    path: '/buying-power-enhanced',
    icon: TrendingUpIcon,
    label: 'SmartBuy Analyzer',
    fullName: 'SmartBuy Analyzer',
    isCustomIcon: false,
    category: 'freemium',
    requiredTier: 'freemium',
    isPro: false
  },
  {
    path: '/mobile-smartbuy-analyzer',
    icon: Smartphone,
    label: 'Analyzer (Mobile)',
    fullName: 'Mobile SmartBuy Analyzer™️',
    isCustomIcon: false,
    category: 'mobile_suite',
    requiredTier: 'freemium',
    isPro: false
  },
  {
    path: '/mobile-smartbuy-comparison',
    icon: Smartphone,
    label: 'Compare (Mobile)',
    fullName: 'Mobile SmartBuy Comparison',
    isCustomIcon: false,
    category: 'mobile_suite',
    requiredTier: 'pro',
    isPro: true
  },
  {
    path: '/mobile-reverse-piti-calculator',
    icon: Smartphone,
    label: 'Reverse PITI (Mobile)',
    fullName: 'Mobile Reverse PITI Calculator',
    isCustomIcon: false,
    category: 'mobile_suite',
    requiredTier: 'freemium',
    isPro: false
  },
  {
    path: '/checklist',
    icon: BookCheckIcon,
    label: 'Checklist',
    fullName: 'Homebuyer Checklist',
    isCustomIcon: false,
    category: 'freemium',
    requiredTier: 'freemium',
    isPro: false
  },
  {
    path: '/inventory',
    icon: Package,
    label: 'Inventory',
    fullName: 'Property Inventory',
    isCustomIcon: false,
    category: 'freemium',
    requiredTier: 'freemium',
    isPro: false
  },
  {
    path: '/homequest',
    icon: Gamepad2,
    label: 'HomeQuest',
    fullName: 'HomeQuest Game',
    isCustomIcon: false,
    category: 'freemium',
    requiredTier: 'freemium',
    isPro: false
  },
  {
    path: '/chatbot',
    icon: MessageSquareIcon,
    label: 'AI Assistant',
    fullName: 'AI Assistant',
    isCustomIcon: false,
    category: 'pro',
    requiredTier: 'pro',
    isPro: true
  },
  {
    path: '/mobile-clara-go',
    icon: MessageSquareIcon,
    label: 'CLARA Go!',
    fullName: 'C.L.A.R.A. Go! Mobile',
    isCustomIcon: false,
    category: 'mobile_suite',
    requiredTier: 'freemium',
    isPro: false
  },
  {
    path: '/mobile-analytics',
    icon: BarChart3Icon,
    label: 'Insights Go!',
    fullName: 'C.L.A.R.A. Insights Go!',
    isCustomIcon: false,
    category: 'mobile_suite',
    requiredTier: 'freemium',
    isPro: false
  },
  {
    path: '/analytics',
    icon: BarChart3Icon,
    label: 'Analytics',
    fullName: 'Analytics Dashboard',
    isCustomIcon: false,
    category: 'tools',
    requiredTier: 'freemium',
    isPro: false
  },
  {
    path: '/unified-calculator',
    icon: CalculatorIcon,
    label: 'Enhanced Calculator',
    fullName: 'Enhanced Calculator',
    isCustomIcon: true,
    category: 'pro',
    requiredTier: 'pro',
    isPro: true
  },
  {
    path: '/smartbuy-analyzer',
    icon: CompassIcon,
    label: 'SmartBuy Comparison',
    fullName: 'SmartBuy Comparison',
    isCustomIcon: false,
    category: 'pro',
    requiredTier: 'pro',
    isPro: true
  },
  {
    path: '/investor-analytics',
    icon: BarChart3Icon,
    label: 'Investment Analytics',
    fullName: 'OfferScope™ Analytics',
    isCustomIcon: false,
    category: 'investor',
    requiredTier: 'investor',
    isPro: true
  },
  {
    path: '/mobile-investor-analytics',
    icon: Smartphone,
    label: 'Mobile Investor Analytics',
    fullName: 'Mobile Investor Analytics',
    isCustomIcon: false,
    category: 'investor',
    requiredTier: 'investor',
    isPro: true
  },
  {
    path: '/market-intelligence',
    icon: TrendingUpIcon,
    label: 'Market Intelligence',
    fullName: 'Market Intelligence Dashboard',
    isCustomIcon: false,
    category: 'investor',
    requiredTier: 'investor',
    isPro: true
  },
  {
    path: '/deal-analyzer',
    icon: BarChart3Icon,
    label: 'Deal Analyzer',
    fullName: 'Investment Deal Analyzer',
    isCustomIcon: false,
    category: 'investor',
    requiredTier: 'investor',
    isPro: true
  },
  {
    path: '/mobile-unified-calculator',
    icon: Smartphone,
    label: 'Unified (Mobile)',
    fullName: 'Mobile Unified Calculator',
    isCustomIcon: false,
    category: 'mobile_suite',
    requiredTier: 'investor',
    isPro: true
  },
  {
    path: '/mobile-homequest',
    icon: Smartphone,
    label: 'HomeQuest (Mobile)',
    fullName: 'Mobile HomeQuest Game',
    isCustomIcon: false,
    category: 'mobile_suite',
    requiredTier: 'freemium',
    isPro: false
  },
  {
    path: '/property-tax-database',
    icon: SearchIcon,
    label: 'Property Tax Database',
    fullName: 'Property Tax Database',
    isCustomIcon: false,
    category: 'agent_pro',
    requiredTier: 'agent_pro',
    isPro: true
  },
  {
    path: '/community-intelligence',
    icon: DatabaseIcon,
    label: 'Community Intelligence',
    fullName: 'Community Intelligence',
    isCustomIcon: false,
    category: 'agent_pro',
    requiredTier: 'agent_pro',
    isPro: true
  },
  {
    path: '/compare',
    icon: ScaleIcon,
    label: 'Compare',
    fullName: 'Property Comparison',
    isCustomIcon: false,
    category: 'agent_pro',
    requiredTier: 'agent_pro',
    isPro: true
  },
  {
    path: '/scraping-dashboard',
    icon: DatabaseIcon,
    label: 'Data Scraping',
    fullName: 'Data Scraping Dashboard',
    isCustomIcon: false,
    category: 'tools',
    requiredTier: 'freemium',
    isPro: false
  },
  {
    path: '/home-comparison-email',
    icon: FileTextIcon,
    label: 'Home Email',
    fullName: 'Home Comparison Email',
    isCustomIcon: false,
    category: 'tools',
    requiredTier: 'pro',
    isPro: true
  },
  {
    path: '/contract-ready-form',
    icon: ClipboardListIcon,
    label: 'Contract Ready Form',
    fullName: 'Contract Ready Form',
    isCustomIcon: false,
    category: 'tools',
    requiredTier: 'freemium',
    isPro: false
  },
  {
    path: '/floor-plans',
    icon: LayoutGridIcon,
    label: 'Floor Plans',
    fullName: 'Floor Plan Management',
    isCustomIcon: false,
    category: 'tools',
    requiredTier: 'freemium',
    isPro: false
  },
  {
    path: '/lots',
    icon: MapPinIcon,
    label: 'Lots',
    fullName: 'Lot Inventory',
    isCustomIcon: false,
    category: 'tools',
    requiredTier: 'freemium',
    isPro: false
  },
  {
    path: '/plat-explorer',
    icon: MapIcon,
    label: 'Plat Explorer',
    fullName: 'Plat Map Explorer',
    isCustomIcon: false,
    category: 'tools',
    requiredTier: 'freemium',
    isPro: false
  },
  {
    path: '/system-diagnostic',
    icon: ActivityIcon,
    label: 'System Diagnostic',
    fullName: 'System Health & Diagnostics',
    isCustomIcon: false,
    category: 'tools',
    requiredTier: 'freemium',
    isPro: false
  },
  {
    path: '/clara-analytics',
    icon: BarChart3Icon,
    label: 'CLARA Analytics',
    fullName: 'CLARA Analytics Dashboard',
    isCustomIcon: false,
    category: 'tools',
    requiredTier: 'agent_pro',
    isPro: true
  },
  {
    path: '/admin',
    icon: ShieldCheckIcon,
    label: 'Admin Dashboard',
    fullName: 'Administration Dashboard',
    isCustomIcon: false,
    category: 'admin',
    requiredTier: 'enterprise',
    isPro: true
  },
  {
    path: '/admin/organizations',
    icon: Building2,
    label: 'Organizations',
    fullName: 'White-Label Organizations',
    isCustomIcon: false,
    category: 'admin',
    requiredTier: 'enterprise',
    isPro: true
  },
  {
    path: '/admin/widgets',
    icon: Code2Icon,
    label: 'Widgets',
    fullName: 'Calculator Widgets',
    isCustomIcon: false,
    category: 'admin',
    requiredTier: 'enterprise',
    isPro: true
  },
  {
    path: '/leads',
    icon: UsersIcon,
    label: 'Leads',
    fullName: 'Leads Dashboard',
    isCustomIcon: false,
    category: 'admin',
    requiredTier: 'enterprise',
    isPro: true
  },
  {
    path: '/admin/patent-tracker',
    icon: ScrollTextIcon,
    label: 'Patent Tracker',
    fullName: 'IP & Patent Management',
    isCustomIcon: false,
    category: 'admin',
    requiredTier: 'enterprise',
    isPro: true
  },
  {
    path: '/admin/marketing-strategy',
    icon: Megaphone,
    label: 'Marketing Strategy',
    fullName: 'Marketing Strategy & Training',
    isCustomIcon: false,
    category: 'admin',
    requiredTier: 'enterprise',
    isPro: true,
    requiredRole: ['admin', 'owner']
  },
  {
    path: '/admin/database-maintenance',
    icon: DatabaseIcon,
    label: 'DB Maintenance',
    fullName: 'Database Maintenance',
    isCustomIcon: false,
    category: 'admin',
    requiredTier: 'enterprise',
    isPro: true,
    requiredRole: ['admin', 'owner']
  },
  {
    path: '/admin/security-monitoring',
    icon: ShieldCheckIcon,
    label: 'Security Monitor',
    fullName: 'Security Monitoring Dashboard',
    isCustomIcon: false,
    category: 'admin',
    requiredTier: 'enterprise',
    isPro: true,
    requiredRole: ['admin', 'owner']
  },
  {
    path: '/admin/system-status',
    icon: ActivityIcon,
    label: 'System Status',
    fullName: 'System Status Dashboard',
    isCustomIcon: false,
    category: 'admin',
    requiredTier: 'enterprise',
    isPro: true,
    requiredRole: ['admin', 'owner']
  },
  {
    path: '/admin/data-aggregator',
    icon: DatabaseIcon,
    label: 'Data Aggregator',
    fullName: 'Market Data Aggregator',
    isCustomIcon: false,
    category: 'admin',
    requiredTier: 'enterprise',
    isPro: true,
    requiredRole: ['admin', 'owner']
  },
  {
    path: '/agreement',
    icon: ScrollTextIcon,
    label: 'User Agreement',
    fullName: 'User Agreement',
    isCustomIcon: false,
    category: 'advisory',
    requiredTier: 'freemium',
    isPro: false
  },
  {
    path: '/brochure',
    icon: FileTextIcon,
    label: 'Brochure',
    fullName: 'Product Brochure',
    isCustomIcon: false,
    category: 'advisory',
    requiredTier: 'freemium',
    isPro: false
  },
  {
    path: '/profile',
    icon: UserIcon,
    label: 'Profile',
    fullName: 'User Profile',
    isCustomIcon: false,
    category: 'user',
    requiredTier: 'freemium',
    isPro: false
  },
  {
    path: '/billing',
    icon: CreditCardIcon,
    label: 'Billing',
    fullName: 'Billing & Plans',
    isCustomIcon: false,
    category: 'user',
    requiredTier: 'freemium',
    isPro: false
  },
  {
    path: '/settings',
    icon: SettingsIcon,
    label: 'Settings',
    fullName: 'Account Settings',
    isCustomIcon: false,
    category: 'user',
    requiredTier: 'freemium',
    isPro: false
  },
  {
    path: '/logout',
    icon: LogOutIcon,
    label: 'Sign Out',
    fullName: 'Sign Out',
    isCustomIcon: false,
    category: 'user',
    requiredTier: 'freemium',
    isPro: false,
    isLogout: true
  },
  {
    path: '/about',
    icon: InfoIcon,
    label: 'About',
    fullName: 'About EquiForge',
    isCustomIcon: false,
    category: 'info',
    requiredTier: 'freemium',
    isPro: false
  },
  {
    path: '/legal',
    icon: ShieldIcon,
    label: 'Legal Hub',
    fullName: 'Legal Information',
    isCustomIcon: false,
    category: 'info',
    requiredTier: 'freemium',
    isPro: false
  },
  {
    path: '/user-manual',
    icon: BookOpenIcon,
    label: 'User Manual',
    fullName: 'EquiForge User Manual',
    isCustomIcon: false,
    category: 'info',
    requiredTier: 'freemium',
    isPro: false
  }
];

export const groupedItems = {
  freemium: navItems.filter(item => item.category === 'freemium'),
  mobile_suite: navItems.filter(item => item.category === 'mobile_suite'),
  pro: navItems.filter(item => item.category === 'pro'),
  investor: navItems.filter(item => item.category === 'investor'),
  agent_pro: navItems.filter(item => item.category === 'agent_pro'),
  tools: navItems.filter(item => item.category === 'tools'),
  admin: navItems.filter(item => item.category === 'admin'),
  advisory: navItems.filter(item => item.category === 'advisory'),
  user: navItems.filter(item => item.category === 'user'),
  info: navItems.filter(item => item.category === 'info')
};

export const categoryLabels = {
  freemium: 'Freemium Features',
  mobile_suite: 'Mobile Suite',
  pro: 'Pro Features',
  investor: 'Investor Features',
  agent_pro: 'Agent Pro Features',
  tools: 'Tools',
  admin: 'Administration',
  advisory: 'Customer Advisory Board',
  user: 'Account',
  info: 'Information'
};
